import { fade, makeStyles } from '@material-ui/core/styles'
import {
  CARD_BORDER,
  MUTED_TEXT,
} from 'pages/schedule/SelectLessonTime/redesign/styles'
import {
  CARD_SHADOW,
  SOFT_PANEL_BACKGROUND,
} from 'pages/things-to-do/events/register/registration-with-payment-intents/redesign/styles'
import {
  PGA_COACH_ERROR_BG,
  PGA_COACH_ERROR_TEXT,
} from 'lib/styles/pga-coach-pill-colors'

// Page chrome (background, container, grid, mobile top card, summary card and
// the sticky bottom bar) comes from the step 1 redesign sheet, like step 2, so
// all three steps look identical. This sheet only adds what step 3 needs: the
// "Your time" card, the student row, the payment card and its line items.
// Colors, fonts and buttons come from the `offeringsRefresh` theme.
const card = theme => ({
  backgroundColor: theme.palette.common.white,
  border: `1px solid ${CARD_BORDER}`,
  borderRadius: 24,
  boxShadow: CARD_SHADOW,
  [theme.breakpoints.down('xs')]: {
    borderRadius: 20,
  },
})

const useStyles = makeStyles(theme => ({
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    margin: -1,
    padding: 0,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  // Same behavior as the legacy `WaiverContainer`.
  waiver: {
    display: 'none',
    background: '#fafafa',
    [theme.breakpoints.down('sm')]: {
      background: theme.palette.common.white,
    },
  },
  waiverVisible: {
    display: 'flex',
  },
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
  },

  // "Your time" card
  yourTime: {
    ...card(theme),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(1.75, 2.5),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5, 2),
    },
  },
  yourTimeLabel: {
    display: 'block',
    margin: 0,
    color: MUTED_TEXT,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.12em',
    lineHeight: '16px',
    textTransform: 'uppercase',
  },
  yourTimeValue: {
    margin: theme.spacing(0.25, 0, 0),
    fontSize: 16,
    fontWeight: 600,
    lineHeight: 1.4,
  },

  // Payment card
  payCard: {
    ...card(theme),
    padding: theme.spacing(3.5, 3.5, 3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2.5),
    },
  },
  whoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    borderRadius: 16,
    backgroundColor: SOFT_PANEL_BACKGROUND,
  },
  whoAvatar: {
    flexShrink: 0,
    width: 40,
    height: 40,
    // Same navy tint circle and navy glyph as the group event confirmation icon.
    backgroundColor: fade(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.main,
    fontSize: 15,
    fontWeight: 700,
  },
  whoText: { flex: 1, minWidth: 0 },
  whoName: {
    margin: 0,
    fontSize: 15,
    fontWeight: 700,
    lineHeight: 1.3,
  },
  whoSub: {
    margin: 0,
    color: MUTED_TEXT,
    fontSize: 13,
    lineHeight: 1.4,
    overflowWrap: 'anywhere',
  },
  heading: {
    margin: theme.spacing(3.5, 0, 0),
    fontSize: 26,
    lineHeight: 1.25,
  },
  headingNote: {
    margin: theme.spacing(0.75, 0, 0),
    color: MUTED_TEXT,
    fontSize: 14,
    lineHeight: 1.5,
  },
  skipRow: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(-0.5),
  },
  stripeWrap: {
    marginTop: theme.spacing(2.5),
    minHeight: 24,
  },
  stripeSkeleton: {
    marginTop: theme.spacing(2.5),
  },

  // Line items
  divider: {
    height: 1,
    margin: theme.spacing(3, 0, 2),
    border: 0,
    backgroundColor: CARD_BORDER,
  },
  lineItem: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    margin: 0,
    color: MUTED_TEXT,
    fontSize: 14,
    lineHeight: 1.6,
  },
  dueRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    margin: theme.spacing(0.75, 0, 0),
    color: theme.palette.text.primary,
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.5,
  },
  lineNote: {
    margin: theme.spacing(0.5, 0, 0),
    color: MUTED_TEXT,
    fontSize: 13,
    lineHeight: 1.5,
  },

  // Optional blocks
  optIn: {
    marginTop: theme.spacing(2.5),
  },
  callout: {
    marginTop: theme.spacing(2.5),
  },
  summaryCallout: {
    marginTop: theme.spacing(2),
  },
  inlineError: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 1.5),
    borderRadius: 12,
    backgroundColor: PGA_COACH_ERROR_BG,
    color: PGA_COACH_ERROR_TEXT,
    fontSize: 14,
    lineHeight: 1.4,
    '& svg': { flexShrink: 0, fontSize: 18 },
  },

  // Actions
  actions: {
    marginTop: theme.spacing(2.5),
  },
  // The theme capitalizes button labels; the pay label is a sentence.
  noTransform: {
    '&&, && [class*="MuiButton-label"]': { textTransform: 'none' },
  },
  payButton: {
    width: '100%',
  },
  // Lock + note under the button, like the Virtual Coaching checkout.
  caption: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.75),
    margin: theme.spacing(1.5, 0, 0),
    '& svg': { fontSize: 16, flexShrink: 0 },
    color: MUTED_TEXT,
    fontSize: 12,
    lineHeight: 1.5,
    textAlign: 'center',
  },

  // Errors that replace the whole step
  errorState: {
    ...card(theme),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(4, 3),
    textAlign: 'center',
  },

  // Summary card rows
  factStrong: {
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  totalNote: {
    margin: theme.spacing(0.5, 0, 0),
    color: MUTED_TEXT,
    fontSize: 13,
    lineHeight: 1.5,
    textAlign: 'right',
  },
}))

export default useStyles
